// localStorage を使ったニックネーム永続化アダプタ。
//
// Secret Chat ではユーザが同じ URL（同じパスワード由来の roomId）に再訪したとき、
// 入室フォームのニックネーム欄を自動入力する必要がある (Requirements R7.2, R9.2)。
// この振る舞いをサポートするため、本モジュールでは roomId をキー名前空間に
// 持たせた localStorage アクセスを薄く包む API を提供する。
//
// ## キー設計
//   `secretChat:{roomId}:nickname`
//
//   - roomId は SHA-256 の 64 桁 hex なので衝突せず、別ルームのデータとも分離される。
//   - 名前空間プレフィックス `secretChat:` により、同一オリジン上で他アプリと共存する。
//
// ## エラー方針（CLAUDE.md「エラーを抑制せず根本原因を解決」とのバランス）
//   プライベートモード / 容量超過 (`QuotaExceededError`) / セキュリティ拒否などで
//   localStorage アクセスは失敗しうるが、これは「ユーザ環境側の制約」であり
//   アプリ側で根本原因を修正する余地はない。ユーザ操作（入室フロー）の阻害を避ける
//   ため、本モジュールは例外を捕捉して save/clear は no-op、load は null とする。
//   ただし運用上の可観測性のため `console.warn` で痕跡を残す。

/** localStorage で nickname を引くための roomId スコープのキーを返す。 */
function nicknameKey(roomId: string): string {
  return `secretChat:${roomId}:nickname`;
}

/**
 * 指定 roomId 配下に nickname を保存する。
 *
 * 既に保存されている場合は上書きする。localStorage が利用不能（プライベートモード、
 * 容量超過など）の場合は副作用を持たずに静かに失敗する。
 */
export function saveNickname(roomId: string, nickname: string): void {
  try {
    localStorage.setItem(nicknameKey(roomId), nickname);
  } catch (err) {
    // ユーザフローを阻害しないように握り潰すが、痕跡は残す。
    console.warn('[storage] saveNickname failed', err);
  }
}

/**
 * 指定 roomId 配下に保存された nickname を返す。
 *
 * 未保存・読み取り失敗時はいずれも `null` を返す。空文字保存と未保存は区別される
 * （空文字保存時は `''` を返す）。
 */
export function loadNickname(roomId: string): string | null {
  try {
    return localStorage.getItem(nicknameKey(roomId));
  } catch (err) {
    console.warn('[storage] loadNickname failed', err);
    return null;
  }
}

/**
 * 指定 roomId 配下に保存された nickname を削除する。
 *
 * 未保存でも例外は投げない（冪等）。削除自体が失敗してもユーザに伝播させない。
 */
export function clearNickname(roomId: string): void {
  try {
    localStorage.removeItem(nicknameKey(roomId));
  } catch (err) {
    console.warn('[storage] clearNickname failed', err);
  }
}

// ---------------------------------------------------------------------------
// テーマ選択の永続化
// ---------------------------------------------------------------------------
//
// 「NUDE MOCHA / SAGE BLUR」を切替可能にする UI 刷新 (§5 / §7) に伴い、
// ゲストユーザーでもブラウザ単位で選択を保つために localStorage に保存する。
// 認証済みユーザーは Firestore など別ストアにも保存できるが、本モジュールは
// あくまでフロント側のキャッシュ層として一律 localStorage 経由でアクセスする。

/** サポートされるテーマ ID。 */
export type ThemeId = 'mocha' | 'sage' | 'pastel';

const THEME_KEY = 'secretChat:theme';
const DEFAULT_THEME: ThemeId = 'pastel';

/**
 * 保存されているテーマ ID を返す。未設定 / SSR / 不正値の場合はデフォルト `pastel`。
 */
export function loadTheme(): ThemeId {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return v === 'mocha' || v === 'sage' || v === 'pastel' ? v : DEFAULT_THEME;
  } catch (err) {
    console.warn('[storage] loadTheme failed', err);
    return DEFAULT_THEME;
  }
}

/**
 * テーマ ID を localStorage に保存する。書き込みに失敗してもユーザフローを
 * 阻害しない（次回起動時にデフォルトに戻るだけ）。
 */
export function saveTheme(theme: ThemeId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch (err) {
    console.warn('[storage] saveTheme failed', err);
  }
}
